/**
 * Client-side product analytics entry point (PostHog).
 * Import this from client components / event handlers only.
 */
import posthog from "posthog-js";

import type { AnalyticsEventName } from "./analytics-events";

export interface AnalyticsUserTraits {
  email?: string;
  name?: string;
  /** App (Prisma) user id */
  app_user_id?: string;
  organization_id?: string;
}

function isAnalyticsEnabled(): boolean {
  return typeof window !== "undefined" && !!process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
}

/** Capture a product analytics event (call from user-driven handlers, not useEffect). */
export function trackClientEvent(event: AnalyticsEventName, properties?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled()) return;
  posthog.capture(event, properties);
}

/**
 * Link the current browser session to a stable user id (Supabase auth user id recommended).
 * Call after login/signup; also synced from SupabaseAuthProvider when session + profile load.
 */
export function identifyAnalyticsUser(params: { distinctId: string; traits?: AnalyticsUserTraits }): void {
  if (!isAnalyticsEnabled()) return;
  const { distinctId, traits } = params;
  posthog.identify(distinctId, {
    ...(traits?.email != null ? { email: traits.email } : {}),
    ...(traits?.name != null ? { name: traits.name } : {}),
    ...(traits?.app_user_id != null ? { app_user_id: traits.app_user_id } : {}),
    ...(traits?.organization_id != null ? { organization_id: traits.organization_id } : {}),
  });
}

/** Clear identity on logout (shared workstations). */
export function resetAnalyticsUser(): void {
  if (!isAnalyticsEnabled()) return;
  posthog.reset();
}

export function captureClientException(error: unknown, context?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled()) return;
  posthog.captureException(error, context);
}
